import { useState, useEffect } from 'react';
import { X, Package, FileText, Plus, Minus, AlertTriangle, Save, Loader, Check, ShoppingCart } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import StockWarningBadge from './StockWarningBadge';
import LocationPicker from './LocationPicker';

export default function JobPlannerModal({ job, companyId, onClose, onSave }) {
  // Track whether the user has changed anything since the modal opened so we
  // can confirm before losing their input on an accidental click-outside.
  const [isDirty, setIsDirty] = useState(false);

  const handleRequestClose = () => {
    if (!isDirty) {
      onClose();
      return;
    }
    // Native confirm is intentional — avoid showConfirm async race on backdrop click
    const ok = window.confirm('Discard your changes to this job plan?');
    if (ok) onClose();
  };
  const { colors } = useTheme();
  const { showError, showSuccess } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryStock, setInventoryStock] = useState({});
  const [defaultLocation, setDefaultLocation] = useState(null);

  // Multi-location state
  const [inventoryMode, setInventoryMode] = useState('simple');
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Plan state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [planItems, setPlanItems] = useState([]);
  const [planNotes, setPlanNotes] = useState('');
  const [existingPlan, setExistingPlan] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Mark form dirty when planItems or planNotes change AFTER the initial load.
  useEffect(() => {
    if (initialLoadDone) setIsDirty(true);
  }, [planItems, planNotes]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Item picker
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [itemSearch, setItemSearch] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, [companyId, job.id]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load company settings to check inventory mode
      const { data: company } = await supabaseData
        .from('companies')
        .select('inventory_mode')
        .eq('id', companyId)
        .single();

      if (company?.inventory_mode) {
        setInventoryMode(company.inventory_mode);
      }

      // Load templates
      const { data: templatesData } = await supabaseData
        .from('job_templates')
        .select('*, job_template_items(*)')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      setTemplates(templatesData || []);

      // Load default location
      const { data: location } = await supabaseData
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_default', true)
        .single();

      setDefaultLocation(location);

      // Set selected location to default if not already set
      if (location && !selectedLocationId) {
        setSelectedLocationId(location.id);
      }

      // Load inventory items
      const { data: items } = await supabaseData
        .from('inventory_items')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      setInventoryItems(items || []);

      // Load stock levels for the selected location
      const locationIdToUse = selectedLocationId || location?.id;
      if (locationIdToUse) {
        const { data: stock } = await supabaseData
          .from('inventory_stock')
          .select('*')
          .eq('company_id', companyId)
          .eq('location_id', locationIdToUse);

        const stockMap = {};
        (stock || []).forEach(s => {
          stockMap[s.inventory_item_id] = s.quantity_on_hand || 0;
        });
        setInventoryStock(stockMap);
      }

      // Check for existing plan
      const { data: plan } = await supabaseData
        .from('job_plans')
        .select('*, job_plan_items(*)')
        .eq('job_id', job.id)
        .single();

      if (plan) {
        setExistingPlan(plan);
        setPlanNotes(plan.notes || '');
        setSelectedTemplate(plan.template_id ? templates?.find(t => t.id === plan.template_id) : null);
        setPlanItems(plan.job_plan_items || []);
        // If plan has a saved location, use it
        if (plan.stock_location_id) {
          setSelectedLocationId(plan.stock_location_id);
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
      // Defer so the state updates above flush before we arm dirty-tracking
      setTimeout(() => setInitialLoadDone(true), 0);
    }
  };

  // Reload stock when location changes
  const loadStockForLocation = async (locationId) => {
    if (!locationId) return;

    const { data: stock } = await supabaseData
      .from('inventory_stock')
      .select('*')
      .eq('company_id', companyId)
      .eq('location_id', locationId);

    const stockMap = {};
    (stock || []).forEach(s => {
      stockMap[s.inventory_item_id] = s.quantity_on_hand || 0;
    });
    setInventoryStock(stockMap);
  };

  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId);
    loadStockForLocation(locationId);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Populate plan items from template
    const items = (template.job_template_items || []).map(ti => ({
      id: `temp-${ti.id}`,
      inventory_item_id: ti.inventory_item_id,
      sku_id: ti.sku_id,
      item_type: 'material',
      name: ti.name,
      quantity: ti.quantity,
      unit_cost: ti.unit_cost,
      is_optional: ti.is_optional,
      notes: ti.notes
    }));
    setPlanItems(items);
  };

  const addItemToPlan = (inventoryItem) => {
    setPlanItems(prev => [...prev, {
      id: `temp-${Date.now()}`,
      inventory_item_id: inventoryItem.id,
      item_type: 'material',
      name: inventoryItem.name,
      quantity: 1,
      unit_cost: inventoryItem.unit_cost || 0
    }]);
    setShowItemPicker(false);
    setItemSearch('');
  };

  const addManualItem = () => {
    setPlanItems(prev => [...prev, {
      id: `temp-${Date.now()}`,
      inventory_item_id: null,
      item_type: 'material',
      name: '',
      quantity: 1,
      unit_cost: 0
    }]);
  };

  const updatePlanItem = (index, field, value) => {
    setPlanItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removePlanItem = (index) => {
    setPlanItems(prev => prev.filter((_, i) => i !== index));
  };

  const getStockStatus = (item) => {
    if (!item.inventory_item_id) return null;
    const available = inventoryStock[item.inventory_item_id] || 0;
    const needed = item.quantity || 0;
    return {
      available,
      needed,
      isShort: needed > available
    };
  };

  const getShortItems = () => {
    return planItems.filter(item => {
      const status = getStockStatus(item);
      return status?.isShort;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let planId;

      if (existingPlan) {
        // Update existing plan
        const { error: updateError } = await supabaseData
          .from('job_plans')
          .update({
            template_id: selectedTemplate?.id || null,
            notes: planNotes,
            status: 'planned',
            stock_location_id: selectedLocationId || defaultLocation?.id,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingPlan.id);

        if (updateError) throw updateError;
        planId = existingPlan.id;

        // Delete existing items
        await supabaseData
          .from('job_plan_items')
          .delete()
          .eq('job_plan_id', planId);

      } else {
        // Create new plan
        const { data: newPlan, error: createError } = await supabaseData
          .from('job_plans')
          .insert({
            job_id: job.id,
            template_id: selectedTemplate?.id || null,
            name: selectedTemplate?.name || 'Custom Plan',
            notes: planNotes,
            status: 'planned',
            stock_location_id: selectedLocationId || defaultLocation?.id
          })
          .select()
          .single();

        if (createError) throw createError;
        planId = newPlan.id;
      }

      // Insert plan items
      if (planItems.length > 0) {
        const items = planItems.map((item, index) => ({
          job_plan_id: planId,
          inventory_item_id: item.inventory_item_id || null,
          sku_id: item.sku_id || null,
          item_type: item.item_type || 'material',
          name: item.name,
          quantity: item.quantity || 1,
          unit_cost: item.unit_cost || 0,
          notes: item.notes || null,
          sort_order: index
        }));

        await supabaseData.from('job_plan_items').insert(items);
      }

      showSuccess('Job plan saved');
      setIsDirty(false); // Successful save — close without confirm
      onSave && onSave();
      onClose();

    } catch (error) {
      console.error('Error saving plan:', error);
      showError('Failed to save plan: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const filteredInventoryItems = inventoryItems.filter(item =>
    item.name?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const shortItems = getShortItems();
  const totalMaterialsCost = planItems.reduce((sum, item) => sum + (item.quantity * (item.unit_cost || 0)), 0);

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <Loader size={32} color={colors.accent} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: colors.text }}>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem'
      }}
      onClick={handleRequestClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
              Job Planner
            </h2>
            <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.9rem' }}>
              {job.title || job.job_number}
            </p>
          </div>

          {/* Location Picker (Multi-location mode) */}
          {inventoryMode === 'multi_location' && (
            <div style={{ marginRight: '1rem' }}>
              <LocationPicker
                companyId={companyId}
                selectedLocationId={selectedLocationId}
                onChange={handleLocationChange}
                style={{ minWidth: '180px' }}
              />
            </div>
          )}

          <button
            onClick={handleRequestClose}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>

          {/* Template Selector */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Start from Template (optional)
            </label>
            <select
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const template = templates.find(t => t.id === e.target.value);
                if (template) {
                  handleTemplateSelect(template);
                } else {
                  setSelectedTemplate(null);
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text
              }}
            >
              <option value="">No template - build manually</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.job_template_items?.length || 0} items)
                </option>
              ))}
            </select>
          </div>

          {/* Low Stock Warning */}
          {shortItems.length > 0 && (
            <div style={{
              background: `${colors.warning}20`,
              border: `1px solid ${colors.warning}50`,
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <AlertTriangle size={20} color={colors.warning} />
                <span style={{ color: colors.warning, fontWeight: 'bold' }}>
                  {shortItems.length} item{shortItems.length !== 1 ? 's' : ''} need restocking
                </span>
              </div>
              <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
                {shortItems.map(item => {
                  const status = getStockStatus(item);
                  return (
                    <div key={item.id}>
                      {item.name}: need {status.needed}, have {status.available}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Materials List */}
          <div style={{
            background: colors.bgInput,
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '0.75rem'
            }}>
              <span style={{ color: colors.text, fontWeight: 'bold' }}>
                <Package size={18} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
                Materials ({planItems.length})
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowItemPicker(true)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: colors.accent,
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Package size={14} />
                  Add
                </button>
                <button
                  onClick={addManualItem}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Plus size={14} />
                  Manual
                </button>
              </div>
            </div>

            {planItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                No materials added yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {planItems.map((item, idx) => {
                  const stockStatus = getStockStatus(item);
                  return (
                    <div key={item.id} style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                      padding: '0.75rem',
                      background: stockStatus?.isShort ? `${colors.warning}10` : colors.bgCard,
                      borderRadius: '8px',
                      border: stockStatus?.isShort ? `1px solid ${colors.warning}40` : `1px solid ${colors.border}`
                    }}>
                      {/* Quantity */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <button
                          onClick={() => updatePlanItem(idx, 'quantity', Math.max(1, item.quantity - 1))}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '4px',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            color: colors.text,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Minus size={12} />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updatePlanItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                          style={{
                            width: '50px',
                            padding: '0.35rem',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            color: colors.text,
                            textAlign: 'center'
                          }}
                        />
                        <button
                          onClick={() => updatePlanItem(idx, 'quantity', item.quantity + 1)}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '4px',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            color: colors.text,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Plus size={12} />
                        </button>
                      </div>

                      {/* Name */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updatePlanItem(idx, 'name', e.target.value)}
                          placeholder="Item name"
                          style={{
                            width: '100%',
                            padding: '0.35rem 0.5rem',
                            background: colors.bgInput,
                            border: `1px solid ${colors.border}`,
                            borderRadius: '4px',
                            color: colors.text
                          }}
                        />
                        {stockStatus && (
                          <div style={{
                            fontSize: '0.75rem',
                            marginTop: '0.25rem',
                            color: stockStatus.isShort ? colors.warning : colors.textMuted
                          }}>
                            {stockStatus.isShort ? (
                              <span>
                                <AlertTriangle size={11} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
                                Need {stockStatus.needed}, have {stockStatus.available}
                              </span>
                            ) : (
                              <span>
                                <Check size={11} style={{ marginRight: '0.25rem', verticalAlign: 'text-bottom' }} />
                                In stock: {stockStatus.available}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Cost */}
                      {!isMobile && (
                        <div style={{ position: 'relative', width: '80px' }}>
                          <span style={{
                            position: 'absolute',
                            left: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: colors.textMuted,
                            fontSize: '0.85rem'
                          }}>$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_cost}
                            onChange={(e) => updatePlanItem(idx, 'unit_cost', parseFloat(e.target.value) || 0)}
                            style={{
                              width: '100%',
                              padding: '0.35rem 0.5rem 0.35rem 1.25rem',
                              background: colors.bgInput,
                              border: `1px solid ${colors.border}`,
                              borderRadius: '4px',
                              color: colors.text
                            }}
                          />
                        </div>
                      )}

                      {/* Remove */}
                      <button
                        onClick={() => removePlanItem(idx)}
                        style={{
                          padding: '0.35rem',
                          background: 'none',
                          border: 'none',
                          color: colors.error,
                          cursor: 'pointer'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Total */}
            {totalMaterialsCost > 0 && (
              <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ color: colors.textMuted }}>Estimated Materials Cost:</span>
                <span style={{ color: colors.text, fontWeight: 'bold', fontSize: '1.1rem' }}>
                  ${totalMaterialsCost.toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: colors.text,
              fontWeight: 'bold'
            }}>
              Notes
            </label>
            <textarea
              value={planNotes}
              onChange={(e) => setPlanNotes(e.target.value)}
              placeholder="Additional notes for this job..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                resize: 'vertical'
              }}
            />
          </div>

          {/* Purchase List */}
          {shortItems.length > 0 && (
            <div style={{
              background: `${colors.accent}10`,
              border: `1px solid ${colors.accent}30`,
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <ShoppingCart size={18} color={colors.accent} />
                <span style={{ color: colors.accent, fontWeight: 'bold' }}>Purchase List</span>
              </div>
              <div style={{ fontSize: '0.9rem', color: colors.text }}>
                {shortItems.map(item => {
                  const status = getStockStatus(item);
                  const toOrder = status.needed - status.available;
                  return (
                    <div key={item.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.35rem 0',
                      borderBottom: `1px solid ${colors.border}`
                    }}>
                      <span>{item.name}</span>
                      <span style={{ fontWeight: 'bold' }}>{toOrder}x</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleRequestClose}
              style={{
                flex: 1,
                padding: '1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 2,
                padding: '1rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 'bold',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {saving ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Plan
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Item Picker Modal */}
      {showItemPicker && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem'
          }}
          onClick={() => setShowItemPicker(false)}
        >
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ color: colors.text, fontWeight: 'bold' }}>Add Material</span>
              <button
                onClick={() => setShowItemPicker(false)}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '1rem', borderBottom: `1px solid ${colors.border}` }}>
              <input
                type="text"
                placeholder="Search inventory..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text
                }}
              />
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
              {filteredInventoryItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                  {inventoryItems.length === 0 ? 'No inventory items' : 'No matches found'}
                </div>
              ) : (
                filteredInventoryItems.map(item => {
                  const qty = inventoryStock[item.id] || 0;
                  return (
                    <button
                      key={item.id}
                      onClick={() => addItemToPlan(item)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ color: colors.text }}>{item.name}</div>
                        <div style={{ fontSize: '0.8rem', color: qty <= (item.reorder_point || 0) ? colors.warning : colors.textMuted }}>
                          {qty} in stock
                        </div>
                      </div>
                      {item.unit_cost > 0 && (
                        <span style={{ color: colors.textMuted }}>${item.unit_cost.toFixed(2)}</span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
